import { usePreventScroll } from '@/hooks/usePreventScroll';
import { useTranslation } from '@/i18n';
import {
  ButtonGroup,
  ButtonV2,
  Form,
  IconInfo,
  InlineNotification,
  Loading,
  UploadDrawerLeadingFiles,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames/bind';
import { FC, memo, useCallback } from 'react';
import { ApplicantAndProxyApplicantSection } from './components/ApplicantAndProxyApplicantSection';
import { DetailDrawerContent } from './components/Detail';
import { usePaymentRequestImportDrawer } from './hooks';
import styles from './PaymentRequestImportDrawer.module.scss';
import type {
  PaymentRequestImportDrawerProps,
  PaymentRequestImportForm,
} from './types';

const cx = classNames.bind(styles);

/**
 * Drawer for importing payment request applications from the list (NA-4238).
 * When canAgentEntry is true, shows 申請者 / 代理申請者 section (NA-4368).
 * Logic lives in usePaymentRequestImportDrawer; this component is presentational.
 * success/failure handling.
 */
export const PaymentRequestImportDrawer: FC<PaymentRequestImportDrawerProps> =
  memo(
    ({
      open,
      onClose,
      onImportSuccess,
      canAgentEntry = false,
      importInvoiceReportSampleCsvPaths,
      refetch,
    }) => {
      const { t } = useTranslation();
      const {
        methods,
        handleClose,
        detailDrawerProps,
        hasValidApplicant,
        uploader,
        hasFile,
        isFileValid,
        handleImport,
        errorMsg,
        setErrorMsg,
        isImporting,
      } = usePaymentRequestImportDrawer(
        open,
        onClose,
        onImportSuccess,
        canAgentEntry,
        importInvoiceReportSampleCsvPaths,
        refetch
      );

      const canSubmit =
        hasFile &&
        isFileValid &&
        (canAgentEntry ? hasValidApplicant : true) &&
        !isImporting;
      const onDismissError = useCallback(() => setErrorMsg(''), [setErrorMsg]);
      usePreventScroll(open);
      return (
        <Form<PaymentRequestImportForm> formMethod={methods}>
          <div className={cx('uploader-container')}>
            <UploadDrawerLeadingFiles<PaymentRequestImportForm>
              title={t('invoice_reports.list_import_drawer.title')}
              open={open}
              onClose={handleClose}
              size='slim'
              control={methods.control}
              name='file'
              arrayName='file'
              uploader={uploader}
              uploadFileLimitTotal={1}
              header={
                <>
                  <InlineNotification
                    message={errorMsg}
                    type='error'
                    visible={Boolean(errorMsg)}
                    onClick={onDismissError}
                  />
                  {canAgentEntry && open && (
                    <ApplicantAndProxyApplicantSection
                      control={methods.control}
                      name='target_office_member_id'
                      inputId='import-drawer-target-office-member-id'
                    />
                  )}
                  <div className={cx('header-info')}>
                    <IconInfo size={14} />
                    <span>
                      {t('invoice_reports.list_import_drawer.description')}
                    </span>
                  </div>
                </>
              }
              details={
                <>
                  {isImporting && (
                    <Loading isOverlay size='xlarge' color='default' />
                  )}
                  <DetailDrawerContent {...detailDrawerProps} />
                </>
              }
              footer={
                <ButtonGroup direction='horizontal'>
                  <ButtonV2 isSecondary onClick={handleClose} type='button'>
                    {t('cancel')}
                  </ButtonV2>
                  <ButtonV2
                    color='primary'
                    type='button'
                    isDisabled={!canSubmit}
                    isLoading={isImporting}
                    testId='submit-button'
                    onClick={handleImport}
                  >
                    {t('import_label')}
                  </ButtonV2>
                </ButtonGroup>
              }
            />
          </div>
        </Form>
      );
    }
  );

PaymentRequestImportDrawer.displayName = 'PaymentRequestImportDrawer';
